import { useCallback, useState, useEffect } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Flame, Clock, X, Check } from "lucide-react";
import { FadeUp } from "./Reveal";

const RECIPES = [
  {
    name: "Post-Burn Protein Bowl",
    protein: "42g",
    time: "15 min",
    pairs: "30-Min Burn",
    kcal: 480,
    carbs: "38g",
    fat: "14g",
    image: "https://images.unsplash.com/photo-1512621776951-a57141f2eefd?auto=format&fit=crop&w=600&q=70",
    ingredients: ["150g grilled chicken breast", "1 cup cooked quinoa", "Roasted chickpeas", "Baby spinach & cherry tomatoes", "Tahini-lemon dressing", "Pumpkin seeds"],
  },
  {
    name: "Overnight Oats Power Jar",
    protein: "31g",
    time: "5 min prep",
    pairs: "10-Min Ignition",
    kcal: 410,
    carbs: "52g",
    fat: "12g",
    image: "https://static.prod-images.emergentagent.com/jobs/753a59ae-6295-4a2c-a309-76467ea4b06e/images/f9c78a792e71156d7436c0bb6687164897927b4831449ea8d6d2c517ae5a96d5.png",
    ingredients: ["1/2 cup rolled oats", "1 scoop vanilla whey", "3/4 cup Greek yogurt", "1 tbsp chia seeds", "Mixed berries", "Drizzle of honey"],
  },
  {
    name: "Lean Steak & Veg",
    protein: "39g",
    time: "20 min",
    pairs: "50-Min Full Send",
    kcal: 520,
    carbs: "22g",
    fat: "28g",
    image: "https://static.prod-images.emergentagent.com/jobs/753a59ae-6295-4a2c-a309-76467ea4b06e/images/fbf45fe529d3fa67e6be9fbf480e3d4cf6e39f77776e3b76afe32d26a678927a.png",
    ingredients: ["180g lean sirloin steak", "Asparagus & broccolini", "1/2 avocado", "Quinoa or wild rice", "Olive oil & lemon", "Sea salt & rosemary"],
  },
  {
    name: "Avocado Egg Power Toast",
    protein: "24g",
    time: "8 min",
    pairs: "20-Min Builder",
    kcal: 380,
    carbs: "30g",
    fat: "20g",
    image: "https://images.unsplash.com/photo-1482049016688-2d3e1b311543?auto=format&fit=crop&w=600&q=70",
    ingredients: ["2 slices sourdough", "2 soft-boiled eggs", "1/2 avocado", "Cottage cheese spread", "Chili flakes", "Microgreens"],
  },
  {
    name: "Grilled Chicken & Quinoa",
    protein: "48g",
    time: "25 min",
    pairs: "40-Min Grind",
    kcal: 540,
    carbs: "45g",
    fat: "16g",
    image: "https://images.unsplash.com/photo-1546069901-ba9599a7e63c?auto=format&fit=crop&w=600&q=70",
    ingredients: ["200g chicken breast", "1 cup tri-color quinoa", "Roasted sweet potato", "Grilled zucchini", "Greek yogurt sauce", "Fresh herbs"],
  },
];

export const RecipeCarousel = () => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "start" });
  const [selected, setSelected] = useState(null);
  const prev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const next = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  useEffect(() => {
    if (selected) window.__lenis?.stop();
    else window.__lenis?.start();
    const onKey = (e) => e.key === "Escape" && setSelected(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selected]);

  return (
    <div data-testid="recipe-carousel" className="mt-16">
      <FadeUp>
        <div className="flex items-end justify-between gap-6">
          <div>
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-volt">From The Fit50 Kitchen</p>
            <h3 className="font-display font-extrabold uppercase text-2xl sm:text-3xl mt-3 leading-tight">
              Fuel matched to <span className="text-volt italic">your session</span>
            </h3>
          </div>
          <div className="flex gap-2 shrink-0">
            <button
              data-testid="recipe-prev-button"
              onClick={prev}
              aria-label="Previous recipes"
              className="grid place-items-center h-11 w-11 border border-white/15 text-neutral-300 hover:border-volt hover:text-volt transition-colors duration-200"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              data-testid="recipe-next-button"
              onClick={next}
              aria-label="Next recipes"
              className="grid place-items-center h-11 w-11 border border-white/15 text-neutral-300 hover:border-volt hover:text-volt transition-colors duration-200"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      </FadeUp>

      <FadeUp delay={0.1}>
        <div className="mt-8 overflow-hidden" ref={emblaRef}>
          <div className="flex gap-4">
            {RECIPES.map((r, i) => (
              <button
                key={r.name}
                data-testid={`recipe-card-${i + 1}`}
                onClick={() => setSelected(r)}
                className="flex-none basis-[85%] sm:basis-[46%] lg:basis-[31.5%] group border border-white/10 bg-carbon overflow-hidden hover:border-volt/40 transition-colors duration-300 text-left cursor-pointer"
              >
                <div className="relative h-44 overflow-hidden">
                  <img
                    src={r.image}
                    alt={r.name}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-carbon via-transparent to-transparent" />
                  <span className="absolute top-3 left-3 bg-volt text-white font-mono text-[10px] tracking-widest uppercase font-semibold px-2.5 py-1">
                    {r.protein} Protein
                  </span>
                  <span className="absolute bottom-3 right-3 font-mono text-[9px] tracking-widest uppercase text-neutral-400 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    Tap For Full Recipe
                  </span>
                </div>
                <div className="p-5">
                  <h4 className="font-display font-bold uppercase tracking-wide text-lg leading-tight">{r.name}</h4>
                  <div className="mt-3 flex items-center justify-between">
                    <span className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-widest uppercase text-neutral-500">
                      <Clock className="h-3 w-3" />
                      {r.time}
                    </span>
                    <span className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-widest uppercase text-volt">
                      <Flame className="h-3 w-3" />
                      Pairs: {r.pairs}
                    </span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </FadeUp>

      <AnimatePresence>
        {selected && (
          <motion.div
            data-testid="recipe-detail-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] grid place-items-center bg-obsidian/90 backdrop-blur-md p-4"
            onClick={() => setSelected(null)}
          >
            <motion.div
              initial={{ scale: 0.92, y: 30, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.95, y: 20, opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto border border-volt/40 bg-carbon shadow-[0_0_80px_rgba(255,107,0,0.15)]"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative h-48">
                <img src={selected.image} alt={selected.name} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-carbon via-carbon/40 to-transparent" />
                <button
                  data-testid="recipe-detail-close-button"
                  onClick={() => setSelected(null)}
                  aria-label="Close recipe"
                  className="absolute top-3 right-3 grid place-items-center h-9 w-9 bg-obsidian/80 border border-white/20 text-neutral-300 hover:text-volt hover:border-volt transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
                <div className="absolute bottom-3 left-5 right-5 flex items-end justify-between gap-3">
                  <h3 className="font-display font-black uppercase text-2xl leading-none">{selected.name}</h3>
                  <span className="shrink-0 font-mono text-[10px] tracking-widest uppercase text-volt border border-volt/40 bg-obsidian/80 px-2 py-1">
                    Pairs: {selected.pairs}
                  </span>
                </div>
              </div>

              <div className="p-6">
                <div className="grid grid-cols-4 gap-2">
                  {[["Kcal", selected.kcal], ["Protein", selected.protein], ["Carbs", selected.carbs], ["Fat", selected.fat]].map(([label, val]) => (
                    <div key={label} className="border border-white/10 bg-obsidian p-3 text-center">
                      <p className="font-display font-black text-xl text-volt">{val}</p>
                      <p className="font-mono text-[9px] tracking-widest uppercase text-neutral-500 mt-1">{label}</p>
                    </div>
                  ))}
                </div>

                <p className="mt-6 font-mono text-xs tracking-[0.25em] uppercase text-volt">Ingredients</p>
                <ul className="mt-3 space-y-2.5">
                  {selected.ingredients.map((ing) => (
                    <li key={ing} className="flex items-start gap-3 text-sm text-neutral-300">
                      <Check className="h-4 w-4 text-volt shrink-0 mt-0.5" strokeWidth={3} />
                      {ing}
                    </li>
                  ))}
                </ul>

                <p className="mt-6 pt-4 border-t border-white/10 flex items-center gap-2 font-mono text-[10px] tracking-widest uppercase text-neutral-500">
                  <Clock className="h-3 w-3" />
                  Ready in {selected.time} — full method in the Fit50 app
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
